import type {
  CanvasElementFrom,
  StyleConfigProps,
  VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementInputBinding,
  type CanvasElementPropertiesWithData,
  type CanvasElementWithDirtyFieldProps,
  type CanvasElementWithFeaturesConfigProps,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type FormContainerModelField,
  type FormOrigin,
  type PregenerationType,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputContainerItemActionWithIdModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { type FormActionType } from '@blaze-shared/workflow-domain';

import type {
  ParentChildrenContentOverrides,
  ParentChildrenStyleOverrides,
} from '../../canvas-elements/parent-overrides';

export const FORM_PADDING_GENERATED = '$t.spacingFormPopup';
export const FORM_PADDING_DRAGGED = '$t.spacingForm';

export const FORM_BINDING_KEY: CanvasElementInputBinding = 'value';

export const DEFAULT_SUCCESS_MESSAGE = 'Form Submitted!';

export type FormElement = CanvasElementFrom<'formContainer'>;

export type FormActionsView = 'bottom' | 'header';
export type FormActionsLayout = 'stacked' | 'sideBySide';

type PropsModel = CanvasElementPropertiesWithData &
  CanvasElementWithDirtyFieldProps &
  CanvasElementWithFeaturesConfigProps &
  StyleConfigProps &
  VisibilityElementProps;

export type FormContainerPropsModel = PropsModel & {
  content: {
    title: ElementLabelProps;
    action?: FormActionType;
    /**
     * Suspends field generation while an AI import is placing its fields.
     * Cleared once that generation run settles.
     */
    aiGeneratedFields?: boolean;
    origin: FormOrigin;
    model: FormContainerModelField[];
    pregenerationType?: PregenerationType;
    postAction?: {
      actionType: 'Clear' | 'Page' | 'None';
      /** @deprecated */
      message?: string;
      /** @deprecated */
      pageId?: string;
    };
    formActions: PropertiesConfigInputContainerItemActionWithIdModel[];
    headerActions: PropertiesConfigInputContainerItemActionWithIdModel[];
    actionsView?: FormActionsView;
    noDataMessage: string;
    options?: {
      columnsCount?: number;
      markRequiredFields?: boolean;
      submitOnEnter?: boolean;
      changeOnBlur?: boolean;
      requiredDisableSubmit?: boolean;
      scrollToError?: boolean;
      actionsLayout?: FormActionsLayout;
      /** AI `edit` action's upsert intent.
       * relayed onto the generated submit workflow's `Update` node. */
      addIfMissing?: boolean;
    };
    children?: FormChildrenContentOverrides;
  };
  style: {
    container: {
      title: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      fill: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      visualEffect: PropertiesConfigInputVisualEffectModel;
      noData: {
        minHeight: number;
      };
    };
    titleBlock: ElementTitleStyleProps;
    children?: FormChildrenStyleOverrides;
  };
};

export type FormChildrenContentOverrides = ParentChildrenContentOverrides;

export type FormChildrenStyleOverrides = ParentChildrenStyleOverrides;
